import { queryFromObject } from '@src/utils';
import { db } from '@src/app-pages/data-entry/offline/db';
import { isDuplicateSite } from '@src/app-pages/data-entry/offline/site-duplicate';

import { toast } from 'react-toastify';
import { tSuccess, tError } from '@common/toast/toastHelper';
import { ApiStatuses } from '@src/utils/enums';
import { getCurrentFieldStudyYear } from '@src/app-pages/data-entry/offline/lookup-cache';

const rootUrl = '/psapi/Sites/';

export default {
  name: 'sites',
  getReducer: () => {
    const initialData = {
      pageSize: 20,
      pageNumber: 0,
      totalResults: 0,
      data: [],
      params: {},
    };

    return (state = initialData, { type, payload }) => {
      switch (type) {
        case 'UPDATE_SITE_PARAMS':
          return {
            ...state,
            params: payload,
          };
        case 'SET_SITES_PAGINATION':
          return {
            ...state,
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
          };
        case 'SITES_UPDATED_ITEMS':
          return {
            ...state,
            data: payload.items,
            totalResults: payload.totalCount,
          };
        case 'RESET_SITES':
          return {
            ...state,
            data: [],
            totalResults: 0,
          };
        default:
          return state;
      }
    };
  },

  selectSitesAll: (state) => state.sites,
  selectSitesData: (state) => state.sites.data,
  selectSitesParams: (state) => state.sites.params,
  selectSitesTotalResults: (state) => state.sites.totalResults,
  selectSitesPageSize: (state) => state.sites.pageSize,
  selectSitesPageNumber: (state) => state.sites.pageNumber,

  doSitesLoadData:
    (pwaMode) =>
    async ({ dispatch, store }) => {
      const isOnline = navigator.onLine;
      const project = Number(store.selectUserRole()?.projectCode);
      const fieldOffice = store.selectUserRole()?.officeCode;

      if (isOnline) {
        // if network status is online, run API call
        store.doFetchSites(null, pwaMode);
        return;
      }

      // If network status is offline...
      // Filter local sites appropriately by user's project ID and field office
      // Edge Case: User switches roles when online and doesn't download latest offline data
      const localSites =
        fieldOffice === 'ZZ'
          ? await db.sites.filter((site) => Number(project) === Number(site.projectId)).toArray()
          : await db.sites
              .filter((site) => Number(project) === Number(site.projectId) && fieldOffice === site.fieldoffice)
              .toArray();
      const moriverData = await db.moriver.toArray();
      const searchData = await db.search.toArray();
      const siteHasForms = (site) => {
        const siteKeys = [site?.siteRouteKey ?? site?.siteId ?? site?.site_id ?? site?.siteFid ?? site?.site_fid]
          .filter((value) => value !== undefined && value !== null)
          .map(String);

        const formBelongsToSite = (form) => {
          const formSiteKeys = [form?.siteRouteKey, form?.siteId, form?.site_id, form?.siteFid, form?.site_fid]
            .filter((value) => value !== undefined && value !== null)
            .map(String);

          return formSiteKeys.some((value) => siteKeys.includes(value));
        };
        return moriverData.some(formBelongsToSite) || searchData.some(formBelongsToSite);
      };

      const normalizedSites = localSites.map((site) => {
        const siteId = site?.siteId ?? site?.site_id ?? site?.serverId;
        const siteFid = site?.siteFid ?? site?.site_fid;
        const isExistingSite = siteId !== undefined && siteId !== null && Number(siteId) > 0;
        const siteRouteKey = isExistingSite ? String(siteId) : siteFid;

        return {
          ...site,
          siteId,
          site_id: siteId,
          siteFid,
          site_fid: siteFid,
          siteRouteKey,
          siteDisplayId: isExistingSite ? siteId : String(siteFid ?? '').slice(-3),
          projectId: site?.projectId ?? site?.project_id,
          segmentId: site?.segmentId ?? site?.segment_id,
          sampleUnitType: site?.sampleUnitType ?? site?.sample_unit_type,
          bendRiverMile: site?.bendRiverMile ?? site?.bend_river_mile ?? site?.brm_id,
          editInitials: site?.editInitials ?? site?.edit_initials,
          uploadedBy: site?.uploadedBy ?? site?.uploaded_by,
          bkgColor: siteHasForms(site) ? '#daf2ea' : null,
        };
      });
      // Filter Cached vs Existing Sites normalized data
      const cachedNewSites = normalizedSites.filter((item) => Number(item.siteId) === 0);
      const existingSites = normalizedSites.filter(
        (item) => item.siteId !== undefined && item.siteId !== null && Number(item.siteId) > 0
      );
      // Descending (Newest to Oldest)
      const sortedCachedNewSites = [...cachedNewSites]?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      const sortedExistingSites = [...existingSites]?.sort((a, b) => Number(b.siteId) - Number(a.siteId));

      const sortedSites =
        cachedNewSites?.length > 0 ? [...sortedCachedNewSites, ...sortedExistingSites] : sortedExistingSites;

      dispatch({
        type: 'SITES_UPDATED_ITEMS',
        payload: {
          items: sortedSites,
          totalCount: normalizedSites.length,
        },
      });
    },

  doFetchSites:
    (siteId, pwaMode) =>
    ({ dispatch, store, apiGet }) => {
      dispatch({ type: 'RESET_SITES' });
      const sitesParams = {
        year: getCurrentFieldStudyYear(),
        project: store.selectAuthData()?.role?.projectCode,
        fieldoffice: store.selectAuthData()?.role?.officeCode,
      };
      const filterParams = !pwaMode ? store.selectSitesParams() : sitesParams;
      const pageSize = store.selectSitesPageSize();
      const pageNumber = store.selectSitesPageNumber();

      let actualSiteId = null;
      if (siteId) {
        if (typeof siteId === 'object') {
          actualSiteId = Number(siteId.siteId);
        } else {
          actualSiteId = Number(siteId);
        }
      }

      let url = '';

      if (actualSiteId) {
        const queryById = queryFromObject({
          project: filterParams?.project,
          id: filterParams?.id,
          siteId: actualSiteId,
        });
        url = `${rootUrl}getSites${queryById}`;
      } else {
        const queryAllSites = queryFromObject({
          ...filterParams,
          size: pageSize,
          page: pageNumber,
        });
        url = `${rootUrl}getSites${queryAllSites}`;
      }

      store.doSetLoadingState(true);
      store.doSetLoadingMessage('Fetching Sites...');

      apiGet(url, (err, body) => {
        store.doSetLoadingState(false);
        if (!err && body?.status === ApiStatuses.Success) {
          const siteItems = body?.data?.items ?? [];
          const normalizedSites = siteItems.map((site) => {
            const siteId = site?.siteId ?? site?.site_id;
            const siteFid = site?.siteFid ?? site?.site_fid;
            const isExistingSite = Number(siteId) > 0;

            return {
              ...site,
              siteId,
              site_id: siteId,
              siteFid,
              site_fid: siteFid,
              siteRouteKey: isExistingSite ? String(siteId) : siteFid,
              siteDisplayId: isExistingSite ? siteId : String(siteFid ?? '').slice(-3),
            };
          });
          dispatch({
            type: 'SITES_UPDATED_ITEMS',
            payload: {
              ...body?.data,
              items: normalizedSites,
              totalCount: body?.data?.totalCount ?? normalizedSites.length,
            },
          });
          if (actualSiteId) {
            const selectedSite = normalizedSites[0];
            selectedSite && dispatch({ type: 'UPDATE_BASE_DATA', payload: selectedSite });
          } else {
            dispatch({ type: 'SITES_FETCH_ERROR', payload: err });
          }
        }
      });
    },

  doFindOnlineDuplicateSite:
    (values, options = {}) =>
    ({ apiGet }) =>
      new Promise((resolve, reject) => {
        const pageSize = 100;
        const fetchPage = (pageNumber, existingSites = []) => {
          const query = queryFromObject({
            year: values.year,
            project: values.projectId,
            fieldoffice: values.fieldoffice,
            segmentCode: values.segmentId,
            size: pageSize,
            page: pageNumber,
          });

          const url = `${rootUrl}getSites${query}`;

          apiGet(url, (err, _body) => {
            if (err || _body?.status !== ApiStatuses.Success) {
              reject(err ?? new Error('Failed to check for duplicate sites.'));
              return;
            }

            const items = _body?.data?.items ?? [];
            const allSites = [...existingSites, ...items];
            const duplicate = allSites.find((site) => isDuplicateSite(site, values, options));

            if (duplicate) {
              resolve(duplicate);
              return;
            }

            const totalCount = Number(_body?.data?.totalCount ?? allSites.length);
            if (allSites.length >= totalCount || items.length === 0) {
              resolve(null);
              return;
            }

            fetchPage(pageNumber + 1, allSites);
          });
        };
        fetchPage(0);
      }),

  doAddSite:
    (params, payload, onSuccess) =>
    ({ dispatch, store, apiPost }) => {
      const toastId = toast.loading('Adding site...');

      const url = `${rootUrl}addSite${queryFromObject(params)}`;

      apiPost(url, payload, (err, body) => {
        if (!err && body?.status === ApiStatuses.Success) {
          dispatch({ type: 'SITES_POST_FINISHED' });
          tSuccess(toastId, body?.message);
          store.doFetchSites(body?.data, false);
          onSuccess?.(body?.data);
        } else {
          dispatch({ type: 'SITES_POST_ERROR', payload: err });
          tError(toastId, body?.message);
        }
      });
    },

  doUpdateSite:
    (siteData) =>
    ({ dispatch, apiPut, store }) => {
      const toastId = toast.loading('Saving site data...');

      const url = `${rootUrl}updateSite`;

      apiPut(url, siteData, (err, body) => {
        if (!err && body?.status === ApiStatuses.Success) {
          dispatch({ type: 'SITES_UPDATE_FINISHED' });
          tSuccess(toastId, 'Changes successfully saved!');
          store.doFetchSites(null, false);
        } else {
          dispatch({ type: 'SITES_UPDATE_ERROR', payload: err });
          tError(toastId, 'Failed to save changes. Please try again.');
        }
      });
    },

  doSetSitesPagination:
    ({ pageSize, pageNumber }) =>
    ({ dispatch, store }) => {
      const isOnline = navigator.onLine;
      dispatch({
        type: 'SET_SITES_PAGINATION',
        payload: { pageSize, pageNumber },
      });
      if (isOnline) {
        store.doFetchSites(null, false);
      }
    },

  doUpdateSiteParams:
    (searchParams) =>
    ({ dispatch, store }) => {
      const isOnline = navigator.onLine;
      const paramObj = {
        id: store.selectUserRole()?.id,
        project: store.selectUserRole()?.projectCode,
      };
      dispatch({
        type: 'UPDATE_SITE_PARAMS',
        payload: { ...searchParams, ...paramObj },
      });
      if (!isOnline) return;
      store.doFetchSites(null, false);
      store.doFetchExportsSites({ ...searchParams, ...paramObj });
    },
};
